import { FC, useCallback, useEffect, useMemo } from "react";

import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import { SelectChangeEvent } from "@mui/material/Select";
import {
  convertCurrencyToCurrencySymbol,
  formatCurrencyAmount,
  getField,
  getTicketRates,
  DiscountType,
  DiscountScope,
  validateDiscountAmount,
  validateDiscountScope,
  TicketingFormCategory,
} from "@simplyk/common";
import { UseFormReturn, useWatch } from "react-hook-form";

import { convertDiscountAmount } from "./discountCode.helper";
import { DiscountCodeData } from "./discountCode.types";
import type { StepDiscountCodeFormData } from "./TicketingLiveEditorStepDiscountCode";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { isRequired, isMin, isMax } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { RateOutput } from "@/common/types/trpc";
import { AutocompleteV2Option, DashboardAutocompleteV2 } from "@/dashboard-design-system/DashboardAutocompleteV2";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardCheckbox } from "@/dashboard-design-system/DashboardCheckbox";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardSelect } from "@/dashboard-design-system/Select";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Delete, Info } from "@/icons/outlined";

type DiscountRateOption = AutocompleteV2Option<string> & {
  isPayWhatYouCan?: boolean;
};

interface DiscountCodeRowProps {
  form: UseFormReturn<StepDiscountCodeFormData>;
  index: number;
  onDelete: () => void;
  hideDeleteButton?: boolean;
  key: string | undefined;
  discountCode: Partial<DiscountCodeData>;
  ticketingHasMultipleDates?: boolean;
  checkForDuplicate: (couponBody: string) => boolean;
  availableRates?: RateOutput[];
  formCategory?: TicketingFormCategory;
}

export const DiscountCodeRow: FC<DiscountCodeRowProps> = ({
  form,
  index,
  onDelete,
  key,
  discountCode,
  hideDeleteButton,
  ticketingHasMultipleDates = false,
  checkForDuplicate,
  availableRates = [],
  formCategory,
}) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { organizationCurrency } = useCurrentUserContext();
  const currencySymbol = convertCurrencyToCurrencySymbol(organizationCurrency);
  const { errors } = form.formState;
  const formDiscountError = errors?.discountCodes && errors.discountCodes[index];
  const discountCodeIdField = `discountCodes.${index}.id` as const;
  const discountCodeBodyField = `discountCodes.${index}.body` as const;
  const discountCodeBodyError = formDiscountError?.body;
  const discountCodeAmountField = `discountCodes.${index}.amount` as const;
  const discountCodeAmountError = formDiscountError?.amount;
  const discountCodeLimitField = `discountCodes.${index}.limit` as const;
  const discountCodeLimitError = formDiscountError?.limit;
  const discountCodeUnitField = `discountCodes.${index}.unit` as const;
  const discountCodeUnitError = formDiscountError?.unit;
  const discountCodeSelectedRatesField = `discountCodes.${index}.selectedRates` as const;
  const discountCodeSelectedRatesError = formDiscountError?.selectedRates;
  const discountCodeScopeField = `discountCodes.${index}.scope` as const;
  const discountCodeScopeError = formDiscountError?.scope;

  const watchedValue = useWatch({
    control: form.control,
    name: discountCodeUnitField,
  });

  const scope = useWatch({
    control: form.control,
    name: discountCodeScopeField,
  });

  const selectedRates = useWatch({
    control: form.control,
    name: discountCodeSelectedRatesField,
  });

  const isFullOrder = scope === DiscountScope.FullOrder;
  const isPerTicketAllRates = scope === DiscountScope.PerTicketAllRates;
  const discountApplicationValue = isFullOrder ? "full_order" : "per_ticket";

  const isDollars = watchedValue === DiscountType.Dollars;

  const discountableRates = useMemo(() => getTicketRates(availableRates), [availableRates]);

  const rateOptions: DiscountRateOption[] = discountableRates.map((rate) => {
    const rateField = getField(rate.rateFields, locale);
    return {
      label: rateField?.title ?? "",
      value: rate.id,
      isPayWhatYouCan: Boolean(rate.isPayWhatYouCan),
    };
  });

  const getOptionDisabled = useCallback((option: DiscountRateOption) => Boolean(option.isPayWhatYouCan), []);

  const hasPayWhatYouCanRates = useMemo(
    () => discountableRates.some((rate) => rate.isPayWhatYouCan),
    [discountableRates]
  );

  const shouldShowPwycDisclaimer = hasPayWhatYouCanRates && (isPerTicketAllRates || isFullOrder);

  const isNotDuplicate = useCallback(
    (message: string) => {
      return {
        validate: {
          isDuplicate: (value: string) => {
            if (checkForDuplicate(value)) {
              return message;
            }
            return true;
          },
        },
      };
    },
    [checkForDuplicate]
  );

  const hasRateSelected = useCallback(
    (message: string) => {
      return {
        validate: {
          hasRateSelected: (value: DiscountCodeData["selectedRates"]) => {
            if (scope !== DiscountScope.PerTicketSpecificRates) {
              return true;
            }
            const selectedRateIds = (value ?? []).map((option) => (typeof option === "string" ? option : option.value));
            const validationResult = validateDiscountScope(scope, selectedRateIds);
            return validationResult.isValid || message;
          },
        },
      };
    },
    [scope]
  );

  const isWholeNumberWhenPercentage = useCallback(
    (message: string) => {
      return {
        validate: {
          isWholeNumberWhenPercentage: (value: number) => {
            if (watchedValue !== DiscountType.Percentage) {
              return true;
            }
            return Number.isInteger(Number(value)) || message;
          },
        },
      };
    },
    [watchedValue]
  );

  const isNotExceedingMinRatePrice = useCallback(
    (message: string) => {
      return {
        validate: {
          exceedsMinPrice: (value: number) => {
            const selectedRateIds =
              selectedRates?.map((option) => (typeof option === "string" ? option : option.value)) || [];

            const validationResult = validateDiscountAmount({
              amount: convertDiscountAmount(value, watchedValue),
              type: watchedValue,
              scope: scope || DiscountScope.PerTicketAllRates,
              selectedRateIds,
              availableRates: availableRates.map((rate) => ({
                id: rate.id,
                amount: rate.amount,
                isPayWhatYouCan: rate.isPayWhatYouCan,
                isAddOn: rate.isAddOn,
              })),
            });

            if (!validationResult.isValid) {
              return message;
            }

            return true;
          },
        },
      };
    },
    [watchedValue, availableRates, scope, selectedRates]
  );

  useEffect(() => {
    if (scope === undefined) {
      form.setValue(discountCodeScopeField, DiscountScope.PerTicketAllRates);
    }
  }, [form, discountCodeScopeField, scope]);

  useEffect(() => {
    form.trigger(discountCodeAmountField);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedValue, scope, selectedRates]);

  const handleDiscountApplicationChange = useCallback(
    (e: SelectChangeEvent<unknown>) => {
      const newValue = e.target.value as "per_ticket" | "full_order";
      if (newValue === "full_order") {
        form.setValue(discountCodeScopeField, DiscountScope.FullOrder, { shouldValidate: true, shouldDirty: true });
      } else {
        // When switching to per_ticket, preserve current per_ticket scope or default to ALL_RATES
        const currentScope = form.getValues(discountCodeScopeField);
        if (currentScope === DiscountScope.PerTicketSpecificRates) {
          form.setValue(discountCodeScopeField, DiscountScope.PerTicketSpecificRates, {
            shouldValidate: true,
            shouldDirty: true,
          });
        } else {
          form.setValue(discountCodeScopeField, DiscountScope.PerTicketAllRates, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      }
    },
    [form, discountCodeScopeField]
  );

  const handleApplicableToAllRatesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      form.setValue(
        discountCodeScopeField,
        checked ? DiscountScope.PerTicketAllRates : DiscountScope.PerTicketSpecificRates
      );
    },
    [form, discountCodeScopeField]
  );

  const getApplicableToAllRatesLabel = () => {
    if (formCategory === TicketingFormCategory.Shop) {
      return t("dashboard", "ticketing.discountApplicableToAllRates.shop");
    } else if (formCategory === TicketingFormCategory.MembershipV2) {
      return t("dashboard", "ticketing.discountApplicableToAllRates.membership");
    }
    return t("dashboard", "ticketing.discountApplicableToAllRates");
  };

  const getApplyPerUnitLabel = () => {
    if (formCategory === TicketingFormCategory.Shop) {
      return t("dashboard", "ticketing.discountApplyPerItemPurchased");
    } else if (formCategory === TicketingFormCategory.MembershipV2) {
      return t("dashboard", "ticketing.discountApplyPerTicket.membership");
    }
    return t("dashboard", "ticketing.discountApplyPerTicket");
  };

  const getApplicableRatesPlaceholder = () => {
    if (formCategory === TicketingFormCategory.Shop) {
      return t("dashboard", "ticketing.discountApplicableRatesPlaceholder.shop");
    } else if (formCategory === TicketingFormCategory.MembershipV2) {
      return t("dashboard", "ticketing.discountApplicableRatesPlaceholder.membership");
    }
    return t("dashboard", "ticketing.discountApplicableRatesPlaceholder");
  };

  return (
    <Grid item xs={12} key={key}>
      <Paper
        sx={(theme) => ({
          padding: theme.spacing(2),
          backgroundColor: theme.palette.surface.neutral.supershy,
          boxShadow: "none",
        })}
      >
        <Grid container spacing={1} sx={{ position: "relative" }} alignItems="flex-start">
          <Grid item xs={12}>
            <Grid container justifyContent="space-between">
              <DashboardTypography variant="subtitle1">{`${t("dashboard", "ticketing.discountCode")} #${index + 1}`}</DashboardTypography>
              {!hideDeleteButton && (
                <DashboardIconButton vibe="neutral" onClick={onDelete} data-test="discount-code-delete">
                  <Delete />
                </DashboardIconButton>
              )}
            </Grid>
          </Grid>
          <input type="hidden" {...form.register(discountCodeIdField)} value={discountCode.id} />
          <Grid item xs={12} sm={4}>
            <DashboardTextField
              data-test="discount-code-body"
              name={discountCodeBodyField}
              label={t("dashboard", "ticketing.discountBody")}
              defaultValue={discountCode.body}
              control={form.control}
              errorMessage={discountCodeBodyError && discountCodeBodyError?.message}
              rules={[
                isRequired(t("dashboard", "ticketing.discountBodyIsRequired") as string),
                isNotDuplicate(t("dashboard", "ticketing.discountBodyDuplicate") as string),
              ]}
              placeholder={t("dashboard", "ticketing.discountBodyPlaceholder")}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <DashboardTextField
              data-test="discount-code-amount"
              name={discountCodeAmountField}
              defaultValue={discountCode.amount}
              label={t("dashboard", "ticketing.discountAmount")}
              control={form.control}
              errorMessage={discountCodeAmountError && discountCodeAmountError?.message}
              rules={[
                isRequired(t("dashboard", "ticketing.discountAmountIsRequired") as string),
                isWholeNumberWhenPercentage(t("dashboard", "ticketing.discountAmountPercentageNoDecimals") as string),
                isMin(
                  0,
                  t("dashboard", "ticketing.discountAmountMin", {
                    amount: formatCurrencyAmount({ value: 0, locale, currency: organizationCurrency }),
                  })
                ),
                isMax(isDollars ? 100000 : 100, t("dashboard", "ticketing.discountAmountMax")),
                isNotExceedingMinRatePrice(t("dashboard", "ticketing.discountAmountExceedsMinTicketPrice")),
              ]}
              type="number"
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <DashboardSelect
              data-test="discount-code-type"
              defaultValue={DiscountType.Dollars}
              name={discountCodeUnitField}
              label={t("dashboard", "ticketing.discountType")}
              control={form.control}
              errorMessage={discountCodeUnitError && (t("dashboard", discountCodeUnitError.message as never) as string)}
            >
              <MenuItem value={DiscountType.Percentage} data-test="discount-code-type-percent">
                %
              </MenuItem>
              <MenuItem value={DiscountType.Dollars} data-test="discount-code-type-dollar">
                {currencySymbol}
              </MenuItem>
            </DashboardSelect>
          </Grid>
          <Grid item xs={6} sm={4}>
            <DashboardTextField
              data-test="discount-code-limit"
              inputProps={{
                step: 1,
              }}
              name={discountCodeLimitField}
              defaultValue={discountCode.limit}
              label={t(
                "dashboard",
                ticketingHasMultipleDates ? "ticketing.discountLimitPerDate" : "ticketing.discountLimit"
              )}
              control={form.control}
              errorMessage={discountCodeLimitError && t("dashboard", `${discountCodeLimitError?.message}` as never)}
              type="number"
              placeholder={t("dashboard", "ticketing.discountLimitPlaceholder")}
            />
          </Grid>

          <Grid item xs={12} marginTop={1}>
            <input
              type="hidden"
              {...form.register(discountCodeScopeField, {
                value: scope ?? DiscountScope.PerTicketAllRates,
              })}
            />
            <DashboardSelect
              data-test="discount-code-apply-discount"
              value={discountApplicationValue}
              label={t("dashboard", "ticketing.discountApplyDiscount")}
              errorMessage={discountCodeScopeError && discountCodeScopeError?.message}
              onChange={handleDiscountApplicationChange}
            >
              <MenuItem value="per_ticket">{getApplyPerUnitLabel()}</MenuItem>
              <MenuItem value="full_order">{t("dashboard", "ticketing.discountApplyFullOrder")}</MenuItem>
            </DashboardSelect>
          </Grid>

          {discountableRates.length > 1 && !isFullOrder && (
            <>
              <Grid item xs={12} marginTop={1}>
                <DashboardCheckbox
                  data-test="discount-code-apply-to-all-tickets"
                  checked={isPerTicketAllRates}
                  onChange={handleApplicableToAllRatesChange}
                  label={getApplicableToAllRatesLabel()}
                  errorMessage={discountCodeScopeError && discountCodeScopeError?.message}
                />
              </Grid>

              {!isPerTicketAllRates && (
                <Grid item xs={12} marginTop={1}>
                  <DashboardAutocompleteV2<DiscountRateOption, true, false, false, StepDiscountCodeFormData>
                    data-test="discount-code-rate-selection"
                    name={discountCodeSelectedRatesField}
                    control={form.control}
                    size="medium"
                    errorMessage={discountCodeSelectedRatesError && discountCodeSelectedRatesError?.message}
                    multiple
                    options={rateOptions}
                    placeholder={getApplicableRatesPlaceholder()}
                    getOptionDisabled={getOptionDisabled}
                    rules={[hasRateSelected(t("dashboard", "ticketing.discountNoRatesSelected") as string)]}
                  />
                </Grid>
              )}
            </>
          )}

          {shouldShowPwycDisclaimer && (
            <Grid item xs={12} marginTop={1}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item>
                  <DashboardIcon size="small" vibe="text-neutral-quiet">
                    <Info />
                  </DashboardIcon>
                </Grid>
                <Grid item xs>
                  <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                    {t("dashboard", "ticketing.discountPwycDisclaimer" as never)}
                  </DashboardTypography>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Grid>
  );
};
